/** Shared AdMob adapter id / config key resolvers for third-party waterfall sources. */

export const INMOBI_SOURCE_ID = '7681903010231960328';
export const APPLOVIN_SOURCE_ID = '1063618907739174004';
export const MINTEGRAL_SOURCE_ID = '1357746574408896200';
export const PANGLE_SOURCE_ID = '4069896914521993236';
export const VUNGLE_SOURCE_ID = '1953547073528090325';

export type PartnerAdapterIds = {
    adapterId: string;
    /** Config key → label (for docs / debugging) */
    configs: Record<string, string>;
};

function isIos(platform: string | null | undefined): boolean {
    return String(platform || '').toUpperCase().trim() === 'IOS';
}

function fmt(format: string | null | undefined): string {
    return String(format || '').toUpperCase().trim();
}

/** InMobi waterfall (non-bidding) adapter + account/placement config keys. */
export function resolveInmobiAdapter(
    platform: string | null | undefined,
    format: string | null | undefined,
): PartnerAdapterIds {
    const ios = isIos(platform);
    const f = fmt(format);
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') {
        return ios
            ? {adapterId: '148', configs: {accountId: '215', placementId: '216'}}
            : {adapterId: '147', configs: {accountId: '213', placementId: '214'}};
    }
    if (f === 'NATIVE') {
        return ios
            ? {adapterId: '152', configs: {accountId: '221', placementId: '222'}}
            : {adapterId: '151', configs: {accountId: '219', placementId: '220'}};
    }
    // BANNER / INTERSTITIAL / unknown → Regular SDK
    return ios
        ? {adapterId: '9', configs: {accountId: '175', placementId: '177'}}
        : {adapterId: '8', configs: {accountId: '174', placementId: '176'}};
}

/** AppLovin waterfall (MAX zone) adapter + SDK Key / Bundle / Zone config keys. */
export function resolveApplovinAdapter(
    platform: string | null | undefined,
    format: string | null | undefined,
): PartnerAdapterIds {
    const ios = isIos(platform);
    const f = fmt(format);
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') {
        return ios
            ? {adapterId: '93', configs: {sdkKey: '266', bundleId: '267', zoneId: '318'}}
            : {adapterId: '94', configs: {sdkKey: '269', bundleId: '270', zoneId: '319'}};
    }
    if (f === 'NATIVE') {
        return ios
            ? {adapterId: '178', configs: {sdkKey: '278', bundleId: '279'}}
            : {adapterId: '179', configs: {sdkKey: '281', bundleId: '282'}};
    }
    // BANNER / INTERSTITIAL
    return ios
        ? {adapterId: '176', configs: {sdkKey: '272', bundleId: '273', zoneId: '320'}}
        : {adapterId: '177', configs: {sdkKey: '275', bundleId: '276', zoneId: '321'}};
}

export type MintegralConfigKeys = {
    appKey: string;
    appId: string;
    placementId: string;
    adUnitId: string;
};

/** Mintegral waterfall adapter + App Key / App ID / Placement / Unit keys. */
export function resolveMintegralAdapter(
    platform: string | null | undefined,
    format: string | null | undefined,
): PartnerAdapterIds & {keys: MintegralConfigKeys} {
    const ios = isIos(platform);
    const f = fmt(format);
    const table: Record<string, {adapterId: string; keys: MintegralConfigKeys}> = {
        'ANDROID:BANNER': {
            adapterId: '530',
            keys: {appKey: '587', appId: '588', placementId: '589', adUnitId: '590'},
        },
        'IOS:BANNER': {
            adapterId: '531',
            keys: {appKey: '591', appId: '592', placementId: '593', adUnitId: '594'},
        },
        'ANDROID:INTERSTITIAL': {
            adapterId: '532',
            keys: {appKey: '595', appId: '596', placementId: '597', adUnitId: '598'},
        },
        'IOS:INTERSTITIAL': {
            adapterId: '533',
            keys: {appKey: '599', appId: '600', placementId: '601', adUnitId: '602'},
        },
        'ANDROID:REWARDED': {
            adapterId: '534',
            keys: {appKey: '603', appId: '604', placementId: '605', adUnitId: '606'},
        },
        'IOS:REWARDED': {
            adapterId: '535',
            keys: {appKey: '607', appId: '608', placementId: '609', adUnitId: '610'},
        },
        'ANDROID:NATIVE': {
            adapterId: '536',
            keys: {appKey: '611', appId: '612', placementId: '613', adUnitId: '614'},
        },
        'IOS:NATIVE': {
            adapterId: '537',
            keys: {appKey: '615', appId: '616', placementId: '617', adUnitId: '618'},
        },
        'ANDROID:APP_OPEN': {
            adapterId: '578',
            keys: {appKey: '701', appId: '702', placementId: '703', adUnitId: '704'},
        },
        'IOS:APP_OPEN': {
            adapterId: '579',
            keys: {appKey: '705', appId: '706', placementId: '707', adUnitId: '708'},
        },
    };
    const formatKey =
        f === 'REWARDED_VIDEO' ? 'REWARDED' : f || 'BANNER';
    const key = `${ios ? 'IOS' : 'ANDROID'}:${formatKey}`;
    const hit = table[key] || table[`${ios ? 'IOS' : 'ANDROID'}:BANNER`];
    return {
        adapterId: hit.adapterId,
        keys: hit.keys,
        configs: {
            appKey: hit.keys.appKey,
            appId: hit.keys.appId,
            placementId: hit.keys.placementId,
            adUnitId: hit.keys.adUnitId,
        },
    };
}

/** AdMob format → InMobi placementType. */
export function inmobiPlacementType(format: string | null | undefined): string {
    const f = fmt(format);
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') return 'REWARDED_VIDEO';
    if (f === 'NATIVE') return 'NATIVE';
    if (f === 'INTERSTITIAL') return 'INTERSTITIAL';
    return 'BANNER';
}

/** AdMob format → MAX ad_format. */
export function maxAdFormat(format: string | null | undefined): string {
    const f = fmt(format);
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') return 'REWARDED';
    if (f === 'NATIVE') return 'NATIVE';
    if (f === 'INTERSTITIAL') return 'INTER';
    if (f === 'MREC') return 'MREC';
    return 'BANNER';
}

/** AdMob format → Mintegral placement ad_type. */
export function mintegralAdType(format: string | null | undefined): string {
    const f = fmt(format);
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') return 'rewarded_video';
    if (f === 'NATIVE') return 'native';
    if (f === 'INTERSTITIAL') return 'interstitial';
    if (f === 'APP_OPEN') return 'splash';
    return 'banner';
}

/** Pangle waterfall adapter + App ID / Ad Placement ID config keys. */
export function resolvePangleAdapter(
    platform: string | null | undefined,
    format: string | null | undefined,
): PartnerAdapterIds {
    const ios = isIos(platform);
    const f = fmt(format);
    const formatKey = f === 'REWARDED_VIDEO' ? 'REWARDED' : f || 'BANNER';
    const table: Record<string, PartnerAdapterIds> = {
        'ANDROID:BANNER': {adapterId: '556', configs: {appId: '645', placementId: '646'}},
        'IOS:BANNER': {adapterId: '557', configs: {appId: '647', placementId: '648'}},
        'ANDROID:INTERSTITIAL': {adapterId: '558', configs: {appId: '649', placementId: '650'}},
        'IOS:INTERSTITIAL': {adapterId: '559', configs: {appId: '651', placementId: '652'}},
        'ANDROID:REWARDED': {adapterId: '560', configs: {appId: '653', placementId: '654'}},
        'IOS:REWARDED': {adapterId: '561', configs: {appId: '655', placementId: '656'}},
        'ANDROID:NATIVE': {adapterId: '562', configs: {appId: '657', placementId: '658'}},
        'IOS:NATIVE': {adapterId: '563', configs: {appId: '659', placementId: '660'}},
        'ANDROID:APP_OPEN': {adapterId: '554', configs: {appId: '641', placementId: '642'}},
        'IOS:APP_OPEN': {adapterId: '555', configs: {appId: '643', placementId: '644'}},
    };
    const key = `${ios ? 'IOS' : 'ANDROID'}:${formatKey}`;
    return table[key] || table[`${ios ? 'IOS' : 'ANDROID'}:BANNER`];
}

/** Vungle / Liftoff Monetize waterfall adapter + Application ID / Placement Reference ID. */
export function resolveVungleAdapter(
    platform: string | null | undefined,
    format: string | null | undefined,
): PartnerAdapterIds {
    const ios = isIos(platform);
    const f = fmt(format);
    const formatKey = f === 'REWARDED_VIDEO' ? 'REWARDED' : f || 'BANNER';
    const table: Record<string, PartnerAdapterIds> = {
        'ANDROID:BANNER': {adapterId: '121', configs: {appId: '172', placementId: '258'}},
        'IOS:BANNER': {adapterId: '122', configs: {appId: '173', placementId: '259'}},
        'ANDROID:INTERSTITIAL': {adapterId: '121', configs: {appId: '172', placementId: '258'}},
        'IOS:INTERSTITIAL': {adapterId: '122', configs: {appId: '173', placementId: '259'}},
        'ANDROID:REWARDED': {adapterId: '143', configs: {appId: '207', placementId: '260'}},
        'IOS:REWARDED': {adapterId: '144', configs: {appId: '208', placementId: '261'}},
        'ANDROID:NATIVE': {adapterId: '475', configs: {appId: '507', placementId: '508'}},
        'IOS:NATIVE': {adapterId: '476', configs: {appId: '509', placementId: '510'}},
        'ANDROID:APP_OPEN': {adapterId: '586', configs: {appId: '709', placementId: '710'}},
        'IOS:APP_OPEN': {adapterId: '587', configs: {appId: '711', placementId: '712'}},
    };
    const key = `${ios ? 'IOS' : 'ANDROID'}:${formatKey}`;
    return table[key] || table[`${ios ? 'IOS' : 'ANDROID'}:BANNER`];
}

/** AdMob format → Pangle ad_slot_type. */
export function pangleAdSlotType(format: string | null | undefined): number {
    const f = fmt(format);
    if (f === 'NATIVE') return 1;
    if (f === 'BANNER') return 2;
    if (f === 'APP_OPEN') return 3;
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') return 5;
    if (f === 'INTERSTITIAL') return 6;
    return 2;
}

/** AdMob format → Vungle placement type. */
export function vunglePlacementType(format: string | null | undefined): string {
    const f = fmt(format);
    if (f === 'REWARDED' || f === 'REWARDED_VIDEO') return 'rewarded';
    if (f === 'NATIVE') return 'native';
    if (f === 'INTERSTITIAL') return 'interstitial';
    if (f === 'MREC') return 'mrec';
    return 'banner';
}

export function maxPlatform(platform: string | null | undefined): string {
    return isIos(platform) ? 'ios' : 'android';
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') return null;
    return value as Record<string, unknown>;
}

/**
 * Normalize apply payloads from admob-api dry-run/apply wrappers.
 * InMobi returns: { applied, result: { success, data: { placementId } } }
 * Some partners may return the resource on `result` or top-level.
 */
function unwrapApplyResource(data: unknown): Record<string, unknown> | null {
    const root = asRecord(data);
    if (!root) return null;
    const result = asRecord(root.result);
    if (result) {
        return asRecord(result.data) || result;
    }
    return asRecord(root.data) || root;
}

export function parseInmobiPlacementIdFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) return '';
    return String(
        record.placementId || record.placement_id || record.id || '',
    ).trim();
}

export function parseMaxAdUnitIdFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) return '';
    return String(record.id || record.ad_unit_id || record.adUnitId || '').trim();
}

export function parseMintegralUnitIdFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) return '';
    return String(record.unit_id || record.unitId || record.id || '').trim();
}

export function parsePanglePlacementIdFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) return '';
    return String(
        record.placement_id ||
            record.placementId ||
            record.ad_slot_id ||
            record.adSlotId ||
            record.id ||
            '',
    ).trim();
}

export function parseVunglePlacementIdFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) return '';
    return String(
        record.referenceId ||
            record.reference_id ||
            record.id ||
            record.placement_id ||
            record.placementId ||
            '',
    ).trim();
}

/** True when mapping change already has at least one non-empty config value. */
export function mappingChangeHasConfigs(change: unknown): boolean {
    if (!change || typeof change !== 'object') return false;
    const configs = (change as Record<string, unknown>).adUnitConfigurations;
    if (!configs || typeof configs !== 'object') return false;
    return Object.values(configs as Record<string, unknown>).some(
        (v) => String(v || '').trim().length > 0,
    );
}
